import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { Bot, Code2, Brain, Sparkles, Zap, GitBranch } from 'lucide-react';
import { cn } from '@/lib/utils';

const features = [
    {
        title: "Natural Language to Code",
        description: "Simply describe what you want to build, and Waldo will generate production-ready Python code for data analysis and visualization.",
        icon: Code2,
    },
    {
        title: "Data-Aware Intelligence",
        description: "Waldo understands your data context, suggesting visualizations and analyses that best reveal insights in your datasets.",
        icon: Brain,
    },
    {
        title: "Interactive Refinement",
        description: "Get instant feedback and iterate on your visualizations through natural conversation. Waldo learns from your preferences.",
        icon: Sparkles,
    },
    {
        title: "Best Practices Built-in",
        description: "Every code suggestion follows data science best practices, using modern libraries like Pandas, Plotly, and scikit-learn optimally.",
        icon: Zap,
    },
    {
        title: "Version Control Ready",
        description: "Generated code is clean, documented, and ready for version control. No cleanup needed before committing to your repository.",
        icon: GitBranch,
    }
];

const AgentPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Meet Waldo
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Your AI Data Science Partner
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            Transform your data analysis workflow with an AI assistant that understands your data,
                            generates production-ready code, and helps you build better data apps faster.
                        </p>
                    </div>

                    {/* Demo Section */}
                    <div className="mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl">
                            <div className="flex h-14 items-center justify-between border-b px-4">
                                <div className="flex items-center space-x-3">
                                    <Bot className="h-5 w-5 text-primary" />
                                    <span className="text-sm font-medium">Waldo</span>
                                </div>
                            </div>
                            <div className="grid lg:grid-cols-2 divide-x h-[600px]">
                                {/* Code Editor */}
                                <div className="bg-white p-4 font-mono text-sm overflow-auto">
                                    <pre className="whitespace-pre-wrap text-gray-800">
                                        <code>{`import pandas as pd
import plotly.express as px
import preswald

# Load the dataset
df = pd.read_csv('data/sales.csv')

# Process the data
monthly_sales = df.groupby(['date', 'category'])['revenue'].sum().reset_index()

# Create an interactive visualization
fig = px.line(monthly_sales, 
              x='date', 
              y='revenue',
              color='category',
              title='Monthly Sales by Category')

# Display in Preswald
preswald.plotly(fig)`}</code>
                                    </pre>
                                </div>

                                {/* Chat Interface */}
                                <div className="flex flex-col">
                                    <div className="flex-1 p-4 space-y-4 overflow-auto">
                                        <div className="rounded-md px-2.5 py-1.5 text-xs border bg-gray-50 text-gray-900 border-gray-200">
                                            Create a line chart showing monthly sales trends by product category
                                        </div>
                                        <div className="rounded-md px-2.5 py-1.5 text-xs border bg-white text-gray-900 border-gray-200 shadow-sm">
                                            I'll help you create a line chart to visualize monthly sales trends across different product categories. I'll use Plotly Express for an interactive visualization. Here's the code:
                                        </div>
                                        <div className="rounded-md px-2.5 py-1.5 text-xs border bg-gray-50 text-gray-900 border-gray-200">
                                            Can you add hover tooltips showing the exact revenue values?
                                        </div>
                                        <div className="rounded-md px-2.5 py-1.5 text-xs border bg-white text-gray-900 border-gray-200 shadow-sm">
                                            I'll modify the visualization to include detailed tooltips. The hover data will show the exact revenue values for each category at each time point.
                                        </div>
                                    </div>
                                    <div className="border-t p-3">
                                        <div className="relative">
                                            <textarea
                                                placeholder="Ask Waldo to help you analyze your data..."
                                                className="w-full pr-8 py-1.5 px-2.5 rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-200 resize-none text-xs placeholder:text-gray-400 text-gray-900"
                                                rows={1}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-16">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default AgentPage; 